import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';

// Utility function to get locale from cookie
const getLocale = () => {
  const match = document.cookie.match(/(^|;)\s*locale=([^;]*)/);
  return match ? decodeURIComponent(match[2]) : "en";
};

// Custom hook to fetch Strapi data
const useFetchStrapiData = () => {
  const [data, setData] = useState([]);
  const STRAPI_URL = process.env.REACT_APP_STRAPI_URL || "http://localhost:1337";
  const STRAPI_TOKEN = process.env.REACT_APP_STRAPI_TOKEN;
  const locale = getLocale();

  const url = STRAPI_URL
    ? `${STRAPI_URL}/api/portfolios?sort=ShotDate:desc&pagination[limit]=3&populate=Cover&locale=${locale}`
    : null;

  useEffect(() => {
    const fetchData = async () => {
      if (!url || !STRAPI_TOKEN) return;

      try {
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${STRAPI_TOKEN}`,
            "Content-Type": "application/json",
          },
        });

        const result = await response.json();
        setData(result.data || []);
      } catch (err) {
        console.error("? Fetch error:", err.message);
      }
    };

    fetchData();
  }, [url, STRAPI_TOKEN]);

  return { data };
};

// Helper to convert Abstract array into plain text
const getAbstractText = (abstract) => {
  if (!Array.isArray(abstract)) return "";

  return abstract
    .filter(block =>
      block.type === "paragraph" &&
      Array.isArray(block.children) &&
      block.children.some(child => typeof child.text === "string" && child.text.trim())
    )
    .map(block =>
      block.children
        .map(child => (typeof child.text === "string" ? child.text : ""))
        .join("")
    )
    .join("\n\n"); // optional: separate paragraphs
};

const LatestPhoto = () => {
  const { t } = useTranslation();
  const { data } = useFetchStrapiData();
  const STRAPI_URL = process.env.REACT_APP_STRAPI_URL || "http://localhost:1337";
  const locale = getLocale();

  const getLocalizedCountry = (code) => {
    try {
      return new Intl.DisplayNames([locale], { type: 'region' }).of(code);
    } catch {
      return code;
    }
  };

  return (
    <div className="container mt-5 mt-xl-10">
      <div className="row g-3 g-xl-5 align-items-end">
        <div className="col-lg-8">
          <h3 className="display-3 text-black">
            <strong>{t("photo.sectionTitle", "Latest Projects")}</strong>
          </h3>
        </div>
        <div className="col-lg-4 text-lg-end">
          <a href="/photos" className="eyebrow underline action text-muted">
            {t("global.viewAll", "View all")} <i className="bi bi-arrow-right"></i>
          </a>
        </div>
      </div>

      <div className="row g-3 g-0 mt-2">
        {data.map((item) => {
          const localizedCountry = item.Country ? getLocalizedCountry(item.Country) : "";
          const abstractText = getAbstractText(item.Abstract);

          return (
            <div className="col-12" key={item.id}>
              <a href={`/photos/${item.slug}`} className="card equal-md-2-1 inverted" style={{borderRadius: '0px'}}>
                <div className="card-wrap p-0">
                  <div className="card-header col-12 col-md-8">
                    <h1 style={{ fontWeight: "bold", lineHeight: "90%" }}>{item.title}</h1>
                  </div>
                  <div className="card-footer mt-auto">
                    <h4>
                      {item.place}
                      {localizedCountry && `, ${localizedCountry}`}
                    </h4>
                    <p style={{ lineHeight: "100%" }}>
                      {item.ShotDate && (
                        <>
                          <span style={{ textTransform: "uppercase" }}>
                            {new Date(item.ShotDate).toLocaleDateString(locale, {
                              month: "long",
                              year: "numeric",
                            })}
                          </span>
                          <br />
                        </>
                      )}
                      {abstractText}
                    </p>
                  </div>
                </div>

                <figure
                  className="background background-overlay"
                  style={{
                    backgroundImage: item.Cover?.url
                      ? `url(${STRAPI_URL}${item.Cover.url})`
                      : "url('/default-image.jpg')",
                  }}
                ></figure>
              </a>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LatestPhoto;
